import React, { useEffect, useState } from 'react';
import {
    Box, Button, IconButton, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Typography, Pagination, Chip, Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PeopleIcon from '@mui/icons-material/People';
import { format } from 'date-fns';
import { NoticeService } from '../../services/notice.service';
import { INotice } from '../../interfaces/notice.interface';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import NoticeForm from './NoticeForm';
import NoticeReadersDialog from './NoticeReadersDialog';

const PAGE_SIZE = 10;

const NoticeAdminList: React.FC = () => {
    const toast = useToast();
    const institutionId = AuthService.getSelectedInstitution()!;
    const [notices, setNotices] = useState<INotice[]>([]);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState<INotice | null>(null);
    const [readersOpen, setReadersOpen] = useState(false);
    const [readersNoticeId, setReadersNoticeId] = useState<string | null>(null);

    const fetchList = async (p: number) => {
        setLoading(true);
        try {
            const data = await NoticeService.listForAdmin(institutionId, p, PAGE_SIZE);
            setNotices(data.content);
            setTotalPages(data.totalPages);
        } catch (err) {
            toast.showApiError(err, 'Failed to load notices');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchList(page);
    }, [page]);

    const openCreate = () => { setEditing(null); setFormOpen(true); };
    const openEdit = (n: INotice) => { setEditing(n); setFormOpen(true); };

    const handleDelete = async (n: INotice) => {
        if (!window.confirm(`Delete notice "${n.title}"?`)) return;
        try {
            await NoticeService.delete(n.id, institutionId);
            toast.showSuccess('Notice deleted');
            fetchList(page);
        } catch (err) {
            toast.showApiError(err, 'Failed to delete notice');
        }
    };

    const openReaders = (n: INotice) => { setReadersNoticeId(n.id); setReadersOpen(true); };

    const isExpired = (n: INotice) => new Date(n.expiresAt).getTime() < Date.now();

    return (
        <Box sx={{ p: 3 }} data-help-page="notices.admin.page">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">Manage Notices</Typography>
                <Button variant="contained" onClick={openCreate} data-help="notices.admin.new">New Notice</Button>
            </Box>
            <TableContainer component={Paper} data-help-section="notices.admin.table">
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="notices.admin.col.title">Title</TableCell>
                            <TableCell data-help="notices.admin.col.audience">Audience</TableCell>
                            <TableCell data-help="notices.admin.col.published">Published</TableCell>
                            <TableCell data-help="notices.admin.col.expires">Expires</TableCell>
                            <TableCell data-help="notices.admin.col.reads">Reads</TableCell>
                            <TableCell data-help="notices.admin.col.status">Status</TableCell>
                            <TableCell data-help="notices.admin.col.actions">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={7}><Typography>Loading...</Typography></TableCell></TableRow>
                        ) : notices.length === 0 ? (
                            <TableRow><TableCell colSpan={7}><Typography color="text.secondary">No notices yet.</Typography></TableCell></TableRow>
                        ) : notices.map(n => (
                            <TableRow key={n.id}>
                                <TableCell>{n.title}</TableCell>
                                <TableCell>
                                    {n.audienceType === 'ALL'
                                        ? 'All employees'
                                        : (n.departmentNames.length ? n.departmentNames.join(', ') : 'Departments')}
                                </TableCell>
                                <TableCell>{format(new Date(n.publishAt), 'dd MMM yyyy HH:mm')}</TableCell>
                                <TableCell>{format(new Date(n.expiresAt), 'dd MMM yyyy HH:mm')}</TableCell>
                                <TableCell>{n.readCount ?? 0}</TableCell>
                                <TableCell>
                                    {isExpired(n) ? <Chip label="Expired" size="small" /> : <Chip label="Active" color="success" size="small" />}
                                </TableCell>
                                <TableCell>
                                    <Tooltip title="View readers">
                                        <IconButton onClick={() => openReaders(n)} data-help="notices.admin.action.readers"><PeopleIcon /></IconButton>
                                    </Tooltip>
                                    <Tooltip title="Edit">
                                        <IconButton onClick={() => openEdit(n)} color="primary" data-help="notices.admin.action.edit"><EditIcon /></IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                        <IconButton onClick={() => handleDelete(n)} color="error" data-help="notices.admin.action.delete"><DeleteIcon /></IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Pagination data-help="notices.admin.paging" count={totalPages} page={page + 1} onChange={(_, p) => setPage(p - 1)} />
                </Box>
            )}
            <NoticeForm
                open={formOpen}
                notice={editing}
                onClose={() => setFormOpen(false)}
                onSaved={() => fetchList(page)}
            />
            <NoticeReadersDialog
                open={readersOpen}
                noticeId={readersNoticeId}
                onClose={() => setReadersOpen(false)}
            />
        </Box>
    );
};

export default NoticeAdminList;
